import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { NEWS_ARTICLES, NEWS_CATEGORIES } from '@/mocks/news';

export default function NewsList() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;

  const filtered = useMemo(() => {
    return NEWS_ARTICLES.filter((a) => {
      const matchCat = activeCategory === 'all' || a.category === activeCategory;
      const matchSearch = !searchQuery || a.title.includes(searchQuery) || a.summary.includes(searchQuery) || a.tags.some((t) => t.includes(searchQuery));
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const featured = filtered.filter((a) => a.featured).slice(0, 2);
  const regular = filtered.filter((a) => !a.featured || featured.findIndex((f) => f.id === a.id) === -1);
  const paginated = regular.slice(0, page * PAGE_SIZE);
  const hasMore = paginated.length < regular.length;

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setPage(1);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-12">
      {/* Search + Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-10">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="搜索资讯..."
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {NEWS_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-[#00FF88] text-black'
                  : 'bg-white/5 border border-white/10 text-white/50 hover:border-[#00FF88]/40 hover:text-[#00FF88]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Articles */}
      {featured.length > 0 && activeCategory === 'all' && !searchQuery && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {featured.map((article) => (
            <Link
              key={article.id}
              to={`/news/${article.id}`}
              className="group relative rounded-2xl overflow-hidden border border-white/8 hover:border-[#00FF88]/40 transition-all duration-300 hover:-translate-y-1 block"
            >
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#00FF88]/20 border border-[#00FF88]/40 text-[#00FF88] text-xs font-semibold">精选</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {article.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-xs">{tag}</span>
                  ))}
                </div>
                <h3 className="text-white font-bold text-base md:text-lg leading-snug group-hover:text-[#00FF88] transition-colors duration-200 line-clamp-2">{article.title}</h3>
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-white/40 text-xs">{article.publishDate}</span>
                  <span className="text-white/40 text-xs"><i className="ri-time-line mr-1" />{article.readTime}分钟阅读</span>
                  <span className="text-white/40 text-xs"><i className="ri-eye-line mr-1" />{(article.views / 10000).toFixed(1)}万</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Article Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/5 border border-white/10">
            <i className="ri-search-line text-white/30 text-2xl" />
          </div>
          <p className="text-white/30 text-sm">暂无相关资讯</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginated.map((article) => (
              <Link
                key={article.id}
                to={`/news/${article.id}`}
                className="group rounded-2xl overflow-hidden border border-white/8 hover:border-[#00FF88]/40 transition-all duration-300 hover:-translate-y-1 bg-[#020810] block"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/60 text-[#00FF88] text-xs font-semibold border border-[#00FF88]/30">
                    {NEWS_CATEGORIES.find((c) => c.id === article.category)?.label || article.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-white/90 font-semibold text-sm leading-snug line-clamp-2 group-hover:text-[#00FF88] transition-colors duration-200 mb-3">{article.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-4">{article.summary}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white/30 text-xs">{article.publishDate}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white/30 text-xs"><i className="ri-time-line mr-1" />{article.readTime}min</span>
                      <span className="text-white/30 text-xs"><i className="ri-eye-line mr-1" />{(article.views / 10000).toFixed(1)}万</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-2 px-8 py-3 rounded-full border border-[#00FF88]/30 text-[#00FF88] text-sm font-semibold hover:bg-[#00FF88]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span>加载更多</span>
                <i className="ri-arrow-down-line" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
